<template>
  <v-alert dense border="left" colored-border :type="hold.state === 'unsecured' ? 'warning' : 'info'"
    icon="mdi-shield-lock-outline" elevation="1" class="deposit-hold-alert mx-3 mt-3 mb-0"
    data-testid="deposit-hold-block">
    <div class="d-flex flex-wrap align-center" style="gap: 8px 16px;">
      <div class="flex-grow-1" style="flex-basis: 420px; min-width: 0;">
        <div class="font-weight-medium">{{ $t("bookings.deposit_hold.title") }}</div>
        <div :class="hold.state === 'unsecured' ? 'warning--text font-weight-bold' : ''" data-testid="deposit-hold-state">
          {{ $t(statusKey) }}<template v-if="hold.state === 'unsecured'">: {{ unsecuredReason }}</template>
        </div>
        <div class="d-flex flex-wrap mt-1" style="gap: 4px 16px;">
          <span>{{ $t("bookings.deposit_hold.in_hold") }}: <strong>{{ money(hold.bank_amount) }}</strong></span>
          <span>{{ $t("bookings.deposit_hold.withheld_amount") }}: <strong>{{ money(withheldAmount) }}</strong></span>
          <span v-if="hold.deferred_amount > 0 && hold.retain_until">
            {{ $t("bookings.deposit_hold.deferred_until", { date: formatDate(hold.retain_until) }) }}
          </span>
        </div>
      </div>
      <div class="d-flex flex-wrap justify-end ml-auto" style="gap: 8px;">
        <v-btn v-if="hold.actions && hold.actions.withhold" x-small outlined color="primary"
          :disabled="moneyActionBusy" @click="showWithhold = true" data-testid="deposit-hold-withhold">
          {{ $t("bookings.deposit_hold.withhold") }}
        </v-btn>
        <v-btn v-if="hold.actions && hold.actions.extend" x-small outlined color="primary"
          :disabled="moneyActionBusy" @click="showExtend = true" data-testid="deposit-hold-extend">
          {{ $t("bookings.deposit_hold.extend") }}
        </v-btn>
        <v-btn v-if="hold.actions && hold.actions.close" x-small outlined color="warning"
          :loading="isClosing" :disabled="moneyActionBusy" @click="confirmClose" data-testid="deposit-hold-close">
          {{ $t("bookings.deposit_hold.close") }}
        </v-btn>
      </div>
    </div>

    <v-expansion-panels v-if="activeDeductions.length || (hold.operations && hold.operations.length)" flat class="mt-2">
      <v-expansion-panel v-if="activeDeductions.length">
        <v-expansion-panel-header>{{ $t("bookings.deposit_hold.deductions") }}</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-list dense>
            <v-list-item v-for="row in activeDeductions" :key="row.id">
              <v-list-item-content>
                <v-list-item-title>{{ categoryText(row.category) }} — {{ row.reason }}</v-list-item-title>
                <v-list-item-subtitle>{{ money(row.hold_amount) }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="!row.settled_at">
                <v-btn text x-small color="warning" :loading="busyDeductionIds.includes(row.id)"
                  :disabled="moneyActionBusy" @click="cancelDeduction(row)">
                  {{ $t("bookings.deposit_hold.cancel_deduction") }}
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel v-if="hold.operations && hold.operations.length">
        <v-expansion-panel-header>{{ $t("bookings.deposit_hold.operations") }}</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-simple-table dense>
            <thead><tr><th>{{ $t("custom.type") }}</th><th>{{ $t("custom.amount") }}</th><th>{{ $t("custom.status") }}</th><th>{{ $t("bookings.deposit_hold.bank_answer") }}</th></tr></thead>
            <tbody>
              <tr v-for="operation in hold.operations" :key="operation.id">
                <td>{{ $t(`bookings.deposit_hold.operation_kinds.${operation.kind}`) }}</td>
                <td>{{ operation.amount == null ? $t("bookings.deposit_hold.full_amount") : money(operation.amount) }}</td>
                <td>{{ $t(`bookings.deposit_hold.operation_states.${operation.state}`) }}</td>
                <td>{{ operation.bank_status || operation.error_code || "—" }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <deposit-withhold-dialog v-model="showWithhold" :loading="isWithholding" @submit="submitWithhold" />
    <deposit-extend-dialog v-model="showExtend" :loading="isExtending" :retain-until="hold.retain_until"
      @submit="extendHold" />
  </v-alert>
</template>

<script>
import moment from "moment";
import { nanoid } from "nanoid";
import DepositWithholdDialog from "./DepositWithholdDialog.vue";
import DepositExtendDialog from "./DepositExtendDialog.vue";

function browserStorage(component) {
  if (component.storage) return component.storage;
  try { return typeof window !== "undefined" ? window.sessionStorage : null; } catch (_error) { return null; }
}

export default {
  name: "DepositHoldBlock",
  components: { DepositWithholdDialog, DepositExtendDialog },
  props: {
    bookingId: { type: [Number, String], required: true },
    hold: { type: Object, required: true },
    currency: { type: String, default: "" },
  },
  data() {
    return {
      showWithhold: false,
      showExtend: false,
      isWithholding: false,
      isExtending: false,
      isClosing: false,
      busyDeductionIds: [],
      storage: null,
    };
  },
  computed: {
    statusKey() { return `bookings.deposit_hold.states.${this.hold.state}`; },
    withheldAmount() {
      return (this.hold.deductions || []).filter((row) => !row.canceled_at)
        .reduce((sum, row) => sum + (Number(row.hold_amount) || 0), 0);
    },
    activeDeductions() { return (this.hold.deductions || []).filter((row) => !row.canceled_at); },
    acceptedWithheldAmount() {
      if (this.hold.retention_mode !== "deferred_only") return 0;
      return this.activeDeductions.filter((row) => row.stage === "acceptance")
        .reduce((sum, row) => sum + (Number(row.hold_amount) || 0), 0);
    },
    closingChargeAmount() {
      const rows = this.hold.retention_mode === "deferred_only"
        ? this.activeDeductions.filter((row) => row.stage === "later")
        : this.activeDeductions;
      return rows.reduce((sum, row) => sum + (Number(row.hold_amount) || 0), 0);
    },
    closingReleaseAmount() {
      if (this.hold.available_amount != null) return Number(this.hold.available_amount) || 0;
      return Math.max((Number(this.hold.bank_amount) || 0) - this.closingChargeAmount, 0);
    },
    unsecuredReason() {
      const key = `bookings.deposit_hold.unsecured_reasons.${this.hold.unsecured_reason || "unknown"}`;
      return this.$te && this.$te(key) ? this.$t(key) : this.$t("bookings.deposit_hold.unsecured");
    },
    closeStorageKey() { return `deposit-hold-close:${this.bookingId}`; },
    moneyActionBusy() { return this.isWithholding || this.isExtending || this.isClosing || this.busyDeductionIds.length > 0; },
  },
  methods: {
    headers(extra = {}) { return { headers: { Authorization: this.$store.getters.getAuthToken, ...extra } }; },
    money(value) { return `${Number(value || 0).toLocaleString(this.$i18n.locale)} ${this.currency}`.trim(); },
    formatDate(value) { return value && moment(value).isValid() ? moment(value).format("DD.MM.YYYY") : "—"; },
    categoryText(category) { return this.$t(`bookings.deposit_hold.categories.${category}`); },
    applyHold(hold) { this.$emit("updated", hold); },
    submitWithhold(form) {
      if (this.isWithholding) return Promise.resolve();
      this.isWithholding = true;
      return this.axios.post(`/api/v1/bookings/${this.bookingId}/deposit_hold/deductions`, form, this.headers())
        .then((response) => { this.applyHold(response.data); this.showWithhold = false; })
        .catch((error) => this.setError(error, this.$t("errors.error")))
        .then(() => { this.isWithholding = false; });
    },
    extendHold(retainUntil) {
      if (this.isExtending) return Promise.resolve();
      this.isExtending = true;
      return this.axios.patch(`/api/v1/bookings/${this.bookingId}/deposit_hold`, { retain_until: retainUntil }, this.headers())
        .then((response) => { this.applyHold(response.data); this.showExtend = false; })
        .catch((error) => this.setError(error, this.$t("errors.error")))
        .then(() => { this.isExtending = false; });
    },
    confirmClose() {
      return this.$confirm({
        title: this.$t("bookings.deposit_hold.close_title"),
        text: this.$t("bookings.deposit_hold.close_text", {
          release: this.money(this.closingReleaseAmount),
          charge: this.money(this.closingChargeAmount),
          accepted: this.money(this.acceptedWithheldAmount),
        }),
        icon: "mdi-shield-off-outline",
        confirmButtonText: this.$t("bookings.deposit_hold.close"),
        cancelButtonText: this.$t("custom.cancel"),
      }).then((confirmed) => (confirmed ? this.closeHold() : undefined));
    },
    closeHold() {
      if (this.isClosing) return Promise.resolve();
      const storage = browserStorage(this);
      const key = (storage && storage.getItem(this.closeStorageKey)) || nanoid();
      if (storage) storage.setItem(this.closeStorageKey, key);
      this.isClosing = true;
      return this.axios.post(`/api/v1/bookings/${this.bookingId}/deposit_hold/close`, {}, this.headers({ "Idempotency-Key": key }))
        .then((response) => {
          if (storage) storage.removeItem(this.closeStorageKey);
          this.applyHold(response.data);
        })
        .catch((error) => this.setError(error, this.$t("errors.error")))
        .then(() => { this.isClosing = false; });
    },
    cancelDeduction(row) {
      if (this.moneyActionBusy) return Promise.resolve();
      this.busyDeductionIds = [...this.busyDeductionIds, row.id];
      return this.axios.delete(`/api/v1/bookings/${this.bookingId}/deposit_hold/deductions/${row.id}`, this.headers())
        .then((response) => this.applyHold(response.data))
        .catch((error) => this.setError(error, this.$t("errors.error")))
        .then(() => { this.busyDeductionIds = this.busyDeductionIds.filter((id) => id !== row.id); });
    },
  },
};
</script>
